import { writable } from "svelte/store"

interface UserAge {
  age: number
  format: string
}

interface UserData {
  email: string
  name?: string
  firstName?: string
  lastName?: string
  dateOfBirth?: string | null
  entryId?: string
  completedActionIds?: string[]
  completedActions?: unknown[]
  userFirstName?: string | null
  userLastName?: string | null
  userPhoneNumber?: string | null
  userAge?: UserAge | null
  userTeam?: string | null
}

interface UserState {
  user: UserData | null
  openActionId: string | null
  isLoading: boolean
  actionsNeedingVerificationIds: string[] // Changed to just store IDs
}

const userStore = writable<UserState>({
  user: null,
  openActionId: null,
  isLoading: true,
  actionsNeedingVerificationIds: [], // Initialize empty array
})

export const userStoreActions = {
  setUser: (userData: UserData) => {
    userStore.update((state) => ({ ...state, user: userData }))
    try {
      localStorage.setItem("userInfo", JSON.stringify(userData))
    } catch (error) {
      console.error("Error setting user data:", error)
    }
  },

  clearUser: () => {
    userStore.update((state) => ({ ...state, user: null }))
    try {
      localStorage.removeItem("userInfo")
    } catch (error) {
      console.error("Error clearing user data:", error)
    }
  },

  loadUser: async () => {
    userStore.update((state) => ({ ...state, isLoading: true }))

    const storedUser = localStorage.getItem("userInfo")
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser)
        userStore.update((state) => ({
          ...state,
          user: userData,
        }))
      } catch (error) {
        console.error("Error parsing stored user data:", error)
        try {
          localStorage.removeItem("userInfo")
        } catch (error) {
          console.error("Error removing user data:", error)
        }
      }
    }

    // Simulate a small delay to ensure smooth loading transition
    await new Promise((resolve) => setTimeout(resolve, 500))
    userStore.update((state) => ({ ...state, isLoading: false }))
  },

  setOpenActionId: (id: string | null) => {
    userStore.update((state) => ({ ...state, openActionId: id }))
  },

  setLoading: (isLoading: boolean) => {
    userStore.update((state) => ({ ...state, isLoading }))
  },

  setActionsNeedingVerification: (actionIds: string[]) => {
    userStore.update((state) => ({
      ...state,
      actionsNeedingVerificationIds: actionIds,
    }))
  },

  removeActionNeedingVerification: (actionId: string) => {
    userStore.update((state) => ({
      ...state,
      actionsNeedingVerificationIds: state.actionsNeedingVerificationIds.filter(
        (id) => id !== actionId,
      ),
    }))
  },
}

export default userStore
